import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Filter, Grid3x3, Sparkles, Search, X } from 'lucide-react';
import { useSubcategories } from '../hooks/useSubcategories';
import CategoryIcon from '../components/CategoryIcon';
import { usePageTitle } from '../hooks/usePageTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Catalog = () => {
  usePageTitle('Catálogo');
  const { grouped: subcatByParent, getName: getSubcategoryName } = useSubcategories();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [showSubcategories, setShowSubcategories] = useState(false);
  const [search, setSearch] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category') || 'all';
    const subcategory = params.get('subcategory') || 'all';
    setFilter(category);
    setSubcategoryFilter(subcategory);
    if (subcategory !== 'all') setShowSubcategories(true);
  }, [location.search]);

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    (async () => {
      try {
        setLoading(true);
        let url = `${API_URL}/products`;
        const params = new URLSearchParams();
        if (filter !== 'all') params.append('category', filter);
        if (params.toString()) url += `?${params.toString()}`;

        const response = await axios.get(url, { signal: controller.signal });
        if (!mounted) return;

        let filteredProducts = response.data;
        if (subcategoryFilter !== 'all') {
          filteredProducts = response.data.filter((p) => p.type === subcategoryFilter);
        }
        setProducts(filteredProducts);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Failed to fetch products:', error);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [filter, subcategoryFilter]);

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setSubcategoryFilter('all');
    if (newFilter === 'all') {
      navigate('/catalogo');
    } else {
      navigate(`/catalogo?category=${newFilter}`);
    }
  };

  const handleSubcategoryChange = (subcategory) => {
    setSubcategoryFilter(subcategory);
    const params = new URLSearchParams();
    if (filter !== 'all') {
      params.append('category', filter);
    }
    if (subcategory !== 'all') {
      params.append('subcategory', subcategory);
    }
    navigate(`/catalogo?${params.toString()}`);
  };

  return (
    <div data-testid="catalog-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <h1 data-testid="catalog-title" className="font-heading text-5xl md:text-6xl font-light tracking-tight mb-4">
            Catálogo
          </h1>
          <p className="text-base font-light leading-relaxed text-muted-foreground">
            Explore nossa coleção completa de móveis exclusivos
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="flex items-center gap-3 border border-border px-4 py-3 focus-within:border-primary transition-colors">
            <Search className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
            <input
              type="text"
              data-testid="catalog-search"
              placeholder="Buscar por nome do produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                data-testid="catalog-search-clear"
                className="text-muted-foreground hover:text-primary"
                title="Limpar busca"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>

        {/* Filter */}
        <div className="mb-12 pb-8 border-b border-border">
          <div className="flex items-center justify-center gap-6 mb-8">
            <Filter className="w-4 h-4" strokeWidth={1.5} />
            <button
              data-testid="filter-all"
              onClick={() => handleFilterChange('all')}
              className={`font-accent text-xs uppercase tracking-widest transition-colors duration-300 ${
                filter === 'all' ? 'text-primary border-b border-primary' : 'text-muted-foreground hover:text-primary'
              }`}
            >
              Todos
            </button>
            <button
              data-testid="filter-residential"
              onClick={() => handleFilterChange('residencial')}
              className={`font-accent text-xs uppercase tracking-widest transition-colors duration-300 ${
                filter === 'residencial' ? 'text-primary border-b border-primary' : 'text-muted-foreground hover:text-primary'
              }`}
            >
              Residencial
            </button>
            <button
              data-testid="filter-corporate"
              onClick={() => handleFilterChange('corporativo')}
              className={`font-accent text-xs uppercase tracking-widest transition-colors duration-300 ${
                filter === 'corporativo' ? 'text-primary border-b border-primary' : 'text-muted-foreground hover:text-primary'
              }`}
            >
              Corporativo
            </button>
          </div>

          {/* Subcategories */}
          {filter !== 'all' && (
            <div className="mt-8">
              <button
                onClick={() => setShowSubcategories(!showSubcategories)}
                data-testid="toggle-subcategories"
                className="flex items-center gap-2 mx-auto text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
              >
                <Grid3x3 className="w-4 h-4" strokeWidth={1.5} />
                {showSubcategories ? 'Ocultar' : 'Filtrar por'} Subcategorias
              </button>

              {showSubcategories && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  <button
                    onClick={() => handleSubcategoryChange('all')}
                    data-testid="subcategory-all"
                    className={`p-4 border transition-all duration-300 ${
                      subcategoryFilter === 'all'
                        ? 'border-primary bg-primary text-white'
                        : 'border-border hover:border-primary'
                    }`}
                  >
                    <div className="text-center">
                      <div className="mb-2 flex justify-center">
                        <Sparkles className="w-7 h-7" strokeWidth={1.5} />
                      </div>
                      <p className="text-xs font-accent uppercase tracking-widest">Todos</p>
                    </div>
                  </button>

                  {subcatByParent[filter]?.map((sub) => (
                    <button
                      key={sub.id}
                      onClick={() => handleSubcategoryChange(sub.slug)}
                      data-testid={`subcategory-${sub.slug}`}
                      className={`p-4 border transition-all duration-300 ${
                        subcategoryFilter === sub.slug
                          ? 'border-primary bg-primary text-white'
                          : 'border-border hover:border-primary'
                      }`}
                    >
                      <div className="text-center">
                        <div className="mb-2 flex justify-center">
                          <CategoryIcon
                            subcategory={sub}
                            size={28}
                            className={subcategoryFilter === sub.slug ? 'text-white' : ''}
                          />
                        </div>
                        <p className="text-xs font-accent uppercase tracking-widest">{sub.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div data-testid="loading-state" className="text-center py-20">
            <p className="text-muted-foreground">Carregando produtos...</p>
          </div>
        ) : products.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20">
            <p className="text-muted-foreground mb-8">Nenhum produto encontrado nesta categoria.</p>
            <p className="text-sm text-muted-foreground">Entre como administrador para adicionar produtos.</p>
          </div>
        ) : (() => {
          const q = search.trim().toLowerCase();
          const visible = q
            ? products.filter(
                (p) =>
                  p.name?.toLowerCase().includes(q) ||
                  p.description?.toLowerCase().includes(q)
              )
            : products;
          if (visible.length === 0) {
            return (
              <div data-testid="empty-search-state" className="text-center py-20">
                <p className="text-muted-foreground mb-2">
                  Nenhum produto encontrado para <strong>"{search}"</strong>.
                </p>
                <button
                  onClick={() => setSearch('')}
                  data-testid="empty-search-clear"
                  className="text-xs uppercase tracking-widest text-primary hover:text-primary/70 mt-2"
                >
                  Limpar busca
                </button>
              </div>
            );
          }
          return (
            <>
              {q && (
                <p
                  data-testid="search-result-count"
                  className="text-xs uppercase tracking-widest text-muted-foreground mb-6 text-center"
                >
                  {visible.length} {visible.length === 1 ? 'resultado' : 'resultados'} para "{search}"
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-12">
                {visible.map((product) => (
              <Link
                key={product.id}
                to={`/produto/${product.id}`}
                data-testid={`product-card-${product.id}`}
                className="group block"
              >
                <div className="relative aspect-[4/3] overflow-hidden mb-4 bg-neutral-50">
                  {product.promotional_price && (
                    <div data-testid={`product-promo-badge-${product.id}`} className="absolute top-3 left-3 bg-red-600 text-white px-2 py-1 text-xs font-semibold z-10">
                      -{Math.round(product.discount_percentage)}%
                    </div>
                  )}
                  <img
                    src={product.images[0] || '/placeholder.svg'}
                    alt={product.name}
                    loading="lazy"
                    className="w-full h-full object-contain p-4 transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div>
                  <p className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    {product.category} • {getSubcategoryName(product.type)}
                  </p>
                  <h3 className="font-body text-lg font-normal mb-2 group-hover:text-primary/70 transition-colors duration-300">
                    {product.name}
                  </h3>
                  {product.promotional_price ? (
                    <div className="flex items-baseline gap-2" data-testid={`product-price-${product.id}`}>
                      <span className="text-xs text-muted-foreground line-through">
                        R$ {(product.original_price || product.price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                      <span className="text-base font-medium text-red-600">
                        R$ {product.promotional_price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid={`product-price-${product.id}`}>
                      R$ {product.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default Catalog;